import { Link, useNavigate, useParams } from "react-router-dom";
import { Drawer, Modal, Tooltip, message } from "antd";
import { useEffect, useMemo, useState } from "react";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { careService } from "../../services/careService";
import { olreadiaService } from "../../services/olreadiaService";
import paths from "../../routes/paths";
import { titleCase, toList } from "../professionalProfileUtils";
import ProfessionalProfileDetails from "../ProfessionalProfileDetails";
import UserProfilePage from "../../users/profile/UserProfilePage";
import CareStaffSnapshotPage from "./CareStaffSnapshotPage";
import { ProfileDocumentSection } from "../../users/profile/ProfileDocumentSections";
import useUsers from "../../hooks/userManagement/useUsers";
import GooglePlacesAutocompleteInput from "../../common/GooglePlacesAutocompleteInput";
import useAppConfig from "../../hooks/access-control/useAppConfig";
import { addressFieldsFromPlaceDetails } from "../../utils/googlePlaces";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import CareProfileRolesDrawer from "components/care/CareProfileRolesDrawer";
import { roleSummary } from "components/care/careProfileRoles";

const clean = (value) => String(value || "").trim();
const normalizeCareRoleName = (role) =>
  String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
const hasCareOwnerOrAdminRole = (roles = []) => {
  const roleSet = new Set((Array.isArray(roles) ? roles : [roles]).map(normalizeCareRoleName).filter(Boolean));
  return roleSet.has("care_owner") || roleSet.has("care_admin");
};
const display = (value, fallback = "Not added") => {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ") || fallback;
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};
const initialsFor = (name = "") =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "--";
const statusClass = (status = "") =>
  ["COMPLETE", "COMPLETED", "ACTIVE", "APPROVED"].includes(String(status).toUpperCase())
    ? "green"
    : String(status).toUpperCase() === "IN_PROGRESS"
      ? "blue"
      : "medium";
const structuredAddress = (address = {}) => {
  if (!address || typeof address === "string") {
    return { search: typeof address === "string" ? address : "", addressLine1: typeof address === "string" ? address : "", city: "", county: "", state: "", country: "", zipCode: "", placeId: "" };
  }
  return {
    search: address.search || address.formattedAddress || address.address || address.addressLine1 || "",
    addressLine1: address.addressLine1 || address.street || address.address || "",
    city: address.city || "",
    county: address.county || "",
    state: address.state || "",
    country: address.country || "",
    zipCode: address.zipCode || address.zip || "",
    placeId: address.placeId || "",
    latitude: address.latitude,
    longitude: address.longitude,
  };
};
const addressSummary = (address = {}) => {
  if (!address) return "";
  if (typeof address === "string") return address;
  return [
    address.addressLine1 || address.street || address.address,
    address.city,
    address.county,
    address.state,
    address.country,
    address.zipCode || address.zip,
  ].filter(Boolean).join(", ");
};
const booleanLabel = (value) =>
  typeof value === "boolean" ? (value ? "Yes" : "No") : "N/A";
const dateTimeLabel = (value) => {
  if (!value) return "N/A";
  const normalized = String(value)
    .replace(/\[.*\]$/, "")
    .replace(/(\.\d{3})\d+(?=Z$|[+-]\d{2}:\d{2}$)/, "$1");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};
const documentUrls = (documentData = {}) =>
  [
    ...toList(documentData?.idDocumentUrls),
    ...toList(documentData?.documentUrls),
    ...toList(documentData?.documentPhotoUrls),
    clean(documentData?.frontPhotoUrl || documentData?.frontPhoto),
    clean(documentData?.backPhotoUrl || documentData?.backPhoto),
    ...toList(documentData?.idDocumentIds).filter((value) => /^https?:\/\//i.test(clean(value))),
    ...toList(documentData?.documentIds).filter((value) => /^https?:\/\//i.test(clean(value))),
  ].map(clean).filter(Boolean);
const normalizedWorkAuthorization = (documentData = null) => {
  if (!documentData) return null;
  const urls = documentUrls(documentData);
  if (urls.length) return documentData;
  const legacyUrls = toList(documentData?.idDocumentIds)
    .map(clean)
    .filter((value) => /^https?:\/\//i.test(value));
  return legacyUrls.length ? { ...documentData, idDocumentUrls: legacyUrls } : documentData;
};
const pageLabel = (index) =>
  index === 0 ? "Front / First Page" : index === 1 ? "Back / Second Page" : `Extra Page ${index + 1}`;
const pageName = (_page, index) => pageLabel(index);
const pageIsImage = (page = {}) => /\.(jpg|jpeg|png|webp|gif|heic|heif)(\?|$)/i.test(String(page?.signedUrl || ""));

const isPersonalWorkspace = (workspace = {}) => {
  const accountType = String(workspace?.accountType || "").toUpperCase();
  if (accountType === "PERSONAL" || accountType === "INDIVIDUAL") return true;
  return Boolean(workspace?.id) && accountType !== "ORGANIZATION" && !workspace?.organizationId;
};
const pagesFromUrls = (urls = []) =>
  urls.map((signedUrl, index) => ({
    fileId: signedUrl,
    index,
    signedUrl,
  }));
const shiftOptions = ["Day", "Night", "Any", "Morning", "Afternoon", "Evening", "Overnight", "Live-in", "Weekend", "On call"];
const workDayOptions = ["Weekdays", "Weekends", "Holidays", "Day shifts", "Night shifts", "Any"];
const languageOptions = ["English", "Spanish", "French", "Portuguese", "Arabic", "Amharic", "Swahili", "Chinese", "Tagalog", "Hindi", "Yoruba", "Igbo", "Twi", "Other"];
const addressFields = [
  ["addressLine1", "Street address"],
  ["city", "City"],
  ["county", "County"],
  ["state", "State"],
  ["country", "Country"],
  ["zipCode", "ZIP/postal code"],
];

const userFor = (profile = {}) => profile?.userProfile || profile?.user || {};
const careFor = (profile = {}) => profile?.care || profile?.careData || {};
const profileFor = (profile = {}) => profile?.profile || profile?.profileData || {};
const nameFor = (profile = {}) => {
  const user = userFor(profile);
  const base = profileFor(profile);
  return clean(user.name) ||
    clean(base.fullName) ||
    [base.firstName, base.middleName, base.lastName].map(clean).filter(Boolean).join(" ") ||
    clean(profile.name) ||
    "Care staff";
};

function DetailCard({ icon, title, rows, onEdit }) {
  const normalizedRows = rows.map((row) =>
    Array.isArray(row) ? { label: row[0], value: row[1], onClick: row[2], docTo: row[3], editTo: row[4] } : row,
  );
  const visibleRows = normalizedRows.filter(({ value, onClick, docTo, editTo, actionTo, actionOnClick, snapshotOnClick, snapshotViewOnClick, snapshotUnavailable }) =>
    display(value, "") !== "" || onClick || docTo || editTo || actionTo || actionOnClick || snapshotOnClick || snapshotViewOnClick || snapshotUnavailable,
  );
  return (
    <article className="resident-preview-card tone-care">
      <div className="resident-preview-card__head">
        <span><i className={`ti ${icon}`} /></span>
        <div>
          <strong>{title}</strong>
          <small>{visibleRows.length} details</small>
        </div>
        {onEdit ? (
          <Tooltip title={`Edit ${title}`}>
            <button className="care-staff-section-edit" type="button" onClick={onEdit} aria-label={`Edit ${title}`}>
              <i className="ti ti-edit" />
            </button>
          </Tooltip>
        ) : null}
      </div>
      <dl>
        {(visibleRows.length ? visibleRows : [{ label: "Details", value: "Not added" }]).map(({ label, value, onClick, docTo, editTo, actionTo, actionOnClick, actionLabel, actionIcon = "ti-plus", snapshotOnClick, snapshotViewOnClick, snapshotUnavailable, snapshotSavedAt, snapshotSaving }) => (
          <div className={String(value || "").length > 80 ? "is-wide" : ""} key={label}>
            <dt>{label}</dt>
            <dd>
              {onClick || docTo || editTo ? (
                <span className={`care-staff-id-link ${snapshotOnClick || snapshotViewOnClick || snapshotUnavailable ? "care-staff-profile-links" : ""}`}>
                  {display(value, "") ? <span>{display(value)}</span> : null}
                  <span className="care-staff-profile-action-group is-linked">
                    <small className="care-staff-profile-action-label"><i className="ti ti-link" /> Linked profile</small>
                    <span className="care-staff-profile-action-buttons">
                      {onClick ? (
                        <Tooltip title="Preview linked profile">
                          <button className="care-staff-eye-link" type="button" onClick={onClick} aria-label={`Preview linked ${label}`}>
                            <i className="ti ti-eye" /><span>Preview</span>
                          </button>
                        </Tooltip>
                      ) : null}
                      {docTo ? (
                        <Tooltip title="Open linked profile page">
                          <Link className="care-staff-doc-link" to={docTo} aria-label={`Open linked ${label}`}>
                            <i className="ti ti-file-description" /><span>Open</span>
                          </Link>
                        </Tooltip>
                      ) : null}
                      {editTo ? (
                        <Tooltip title="Edit linked profile">
                          <Link className="care-staff-edit-link" to={editTo} aria-label={`Edit linked ${label}`}>
                            <i className="ti ti-edit" /><span>Edit</span>
                          </Link>
                        </Tooltip>
                      ) : null}
                    </span>
                  </span>
                  <span className={`care-staff-profile-action-group is-snapshot ${snapshotSavedAt ? "has-snapshot" : ""}`}>
                    <small className="care-staff-profile-action-label">
                      <i className="ti ti-archive" />
                      {snapshotSavedAt ? <>Saved at <strong>{dateTimeLabel(snapshotSavedAt)}</strong></> : "Saved snapshot"}
                    </small>
                    <span className="care-staff-profile-action-buttons">
                      {snapshotOnClick ? (
                        <Tooltip title={snapshotSavedAt ? `Replace snapshot saved ${dateTimeLabel(snapshotSavedAt)}` : `Save ${label} snapshot`}>
                          <button className="care-staff-snapshot-link" type="button" onClick={snapshotOnClick} disabled={snapshotSaving} aria-label={`${snapshotSavedAt ? "Replace" : "Save"} ${label} snapshot`}>
                            <i className={`ti ${snapshotSaving ? "ti-loader-2 spin" : "ti-device-floppy"}`} />
                            <span>{snapshotSavedAt ? "Replace" : "Save"}</span>
                          </button>
                        </Tooltip>
                      ) : null}
                      {snapshotViewOnClick ? (
                        <Tooltip title="View saved snapshot">
                          <button className="care-staff-snapshot-view-link" type="button" onClick={snapshotViewOnClick} aria-label={`View saved ${label} snapshot`}>
                            <i className="ti ti-eye" /><span>View saved</span>
                          </button>
                        </Tooltip>
                      ) : null}
                      {snapshotUnavailable ? <span className="care-staff-snapshot-empty"><i className="ti ti-info-circle" /> Not saved</span> : null}
                    </span>
                  </span>
                </span>
              ) : actionTo || actionOnClick ? (
                <span className="care-staff-id-link">
                  <span>{display(value)}</span>
                  <Tooltip title={actionLabel || `Add ${label}`}>
                    {actionTo ? (
                      <Link className="care-staff-edit-link" to={actionTo} aria-label={actionLabel || `Add ${label}`}>
                        <i className={`ti ${actionIcon}`} />
                      </Link>
                    ) : (
                      <button className="care-staff-edit-link" type="button" onClick={actionOnClick} aria-label={actionLabel || `Add ${label}`}>
                        <i className={`ti ${actionIcon}`} />
                      </button>
                    )}
                  </Tooltip>
                </span>
              ) : display(value)}
            </dd>
          </div>
        ))}
      </dl>
    </article>
  );
}

export default function CareStaffDetailsPage() {
  const navigate = useNavigate();
  const { careProfileId } = useParams();
  const { getUserById, getUserSnapshotPayload } = useUsers(0, 20, null, true, { autoFetch: false });
  const { user: authUser, careRoles = [], roles = [], userRoles = [] } = useAuth();
  const { workspaces, selectedWorkspaceId, selectWorkspace } = useWorkspace();
  const { appConfig, loading: appConfigLoading } = useAppConfig();
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey ||
    appConfig?.googleMapsApiMatrixKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    process.env.VITE_GOOGLE_MAPS_API_KEY ||
    "";
  const [profile, setProfile] = useState(null);
  const [relatedUserProfile, setRelatedUserProfile] = useState(null);
  const [drawer, setDrawer] = useState("");
  const [editSection, setEditSection] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [documentPreview, setDocumentPreview] = useState(null);
  const [documentPreviewRotation, setDocumentPreviewRotation] = useState(0);
  const [loading, setLoading] = useState(true);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [collapsedDocumentSections, setCollapsedDocumentSections] = useState({});
  const [error, setError] = useState("");
  const [professionalWorkspaceDrawerOpen, setProfessionalWorkspaceDrawerOpen] = useState(false);
  const [snapshotSaving, setSnapshotSaving] = useState("");
  const [snapshotDrawerType, setSnapshotDrawerType] = useState("");
  const [rolesDrawerOpen, setRolesDrawerOpen] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    careService.careProfiles.get(careProfileId)
      .then((data) => {
        if (!active) return;
        setProfile(data || null);
      })
      .catch((err) => {
        if (!active) return;
        setError(err?.response?.data?.message || err?.message || "Unable to load care staff details.");
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [careProfileId]);

  const view = useMemo(() => {
    const care = careFor(profile);
    const user = userFor(profile);
    const base = profileFor(profile);
    const name = nameFor(profile || {});
    return {
      care,
      user,
      base,
      name,
      initials: initialsFor(name),
      employeeNumber: profile?.employeeNumber || "",
      email: user.email || base.email || profile?.email || "",
      phone: user.phone || base.phone || profile?.phone || "",
      status: String(profile?.onboardingStatus || profile?.status || "IN_PROGRESS").toUpperCase(),
    };
  }, [profile]);
  const userProfileId = profile?.userProfileId || view.user.id || view.user.userId || view.user.uid || "";
  const canViewSensitiveStaffDetails =
    String(authUser?.id || authUser?.userId || authUser?.uid || "") === String(userProfileId || "") ||
    hasCareOwnerOrAdminRole([...careRoles, ...roles, ...userRoles]);
  const canCaptureStaffSnapshots = hasCareOwnerOrAdminRole([...careRoles, ...roles, ...userRoles]);
  const personalWorkspaces = useMemo(
    () => workspaces.filter(isPersonalWorkspace),
    [workspaces],
  );
  const canManageStaffRoles = canCaptureStaffSnapshots;
  const professionalProfileId = profile?.professionalProfileId || "";
  const userProfilePath = userProfileId ? paths.userProfileDetails(userProfileId) : "";
  const professionalProfilePath = professionalProfileId ? paths.professionalProfileDetails(professionalProfileId) : "";
  const editPath = (path) => (path ? `${path}?edit=1` : "");
  const identityVerification = relatedUserProfile?.userIdentityVerification || null;
  const workAuthorization = normalizedWorkAuthorization(relatedUserProfile?.userWorkAuthorization || null);
  const identityPages = pagesFromUrls(documentUrls(identityVerification));
  const workAuthorizationPages = pagesFromUrls(documentUrls(workAuthorization));
  const careProfileRecordId = profile?.id || profile?.careProfileId || careProfileId;
  const userProfileSnapshot = profile?.review?.userProfileSnapshot || null;
  const professionalProfileSnapshot = profile?.review?.professionalProfileSnapshot || null;

  const saveProfileSnapshot = async (kind) => {
    if (!careProfileRecordId) return;
    const isUserProfile = kind === "user";
    if (isUserProfile && !userProfileId) {
      message.error("This care staff record is not linked to a user profile.");
      return;
    }
    if (!isUserProfile && !professionalProfileId) {
      message.error("This care staff record is not linked to a professional profile.");
      return;
    }
    setSnapshotSaving(kind);
    try {
      const source = isUserProfile
        ? await getUserSnapshotPayload(userProfileId)
        : await olreadiaService.professionProfiles.getSnapshotPayload(professionalProfileId);
      if (!source) throw new Error(`Unable to load the ${isUserProfile ? "user" : "professional"} profile.`);
      if (isUserProfile) {
        if (source.profilePhoto && !source.profilePhotoFileId) {
          throw new Error("The profile service did not return the profile photo file ID. Refresh the profile service deployment and try again.");
        }
        if (source.securityPhoto && !source.securityPhotoFileId) {
          throw new Error("The profile service did not return the security photo file ID. Refresh the profile service deployment and try again.");
        }
      }
      const savedSnapshot = await careService.careProfiles.saveSnapshot(
        careProfileRecordId,
        isUserProfile ? "user-profile" : "professional-profile",
        {
          sourceProfileId: isUserProfile ? userProfileId : professionalProfileId,
          data: source,
          filesCloned: true,
        },
        { actorId: authUser?.id || authUser?.userId || authUser?.uid },
      );
      const property = isUserProfile ? "userProfileSnapshot" : "professionalProfileSnapshot";
      setProfile((current) => ({
        ...(current || {}),
        review: { ...(current?.review || {}), [property]: savedSnapshot },
      }));
      if (isUserProfile) setRelatedUserProfile(source);
      message.success(`${isUserProfile ? "User" : "Professional"} profile snapshot saved to this workspace.`);
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || "Unable to save profile snapshot.");
    } finally {
      setSnapshotSaving("");
    }
  };

  const confirmProfileSnapshotSave = (kind) => {
    const isUserProfile = kind === "user";
    const existingSnapshot = isUserProfile ? userProfileSnapshot : professionalProfileSnapshot;
    const profileLabel = isUserProfile ? "user profile" : "professional profile";
    Modal.confirm({
      title: existingSnapshot ? `Replace saved ${profileLabel} snapshot?` : `Save ${profileLabel} snapshot?`,
      content: existingSnapshot
        ? `This will permanently overwrite the snapshot saved ${dateTimeLabel(existingSnapshot.capturedAt)} with the current ${profileLabel} data.`
        : `This will save the current ${profileLabel} data as a workspace-owned snapshot.`,
      okText: existingSnapshot ? "Replace snapshot" : "Save snapshot",
      cancelText: "Cancel",
      okButtonProps: { danger: Boolean(existingSnapshot) },
      onOk: () => saveProfileSnapshot(kind),
    });
  };

  const createProfessionalProfileFromWorkspace = (workspaceId) => {
    if (workspaceId && workspaceId !== selectedWorkspaceId) {
      selectWorkspace(workspaceId);
    }
    setProfessionalWorkspaceDrawerOpen(false);
    navigate(paths.professionalProfileCreate);
  };

  const createPersonalWorkspace = () => {
    setProfessionalWorkspaceDrawerOpen(false);
    navigate(paths.workspaceCreate(), {
      state: {
        accountType: "PERSONAL",
        nextPath: paths.professionalProfileCreate,
      },
    });
  };

  useEffect(() => {
    let active = true;
    setRelatedUserProfile(null);
    if (!userProfileId || !canViewSensitiveStaffDetails) {
      setDocumentsLoading(false);
      return () => {
        active = false;
      };
    }
    setDocumentsLoading(true);
    getUserById(userProfileId)
      .then((data) => {
        if (active) setRelatedUserProfile(data || null);
      })
      .catch(() => {
        if (active) setRelatedUserProfile(null);
      })
      .finally(() => {
        if (active) setDocumentsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [canViewSensitiveStaffDetails, getUserById, userProfileId]);

  const openDocumentPreview = (page, index, title, galleryPages = []) => {
    if (!page?.signedUrl) return;
    setDocumentPreviewRotation(0);
    setDocumentPreview({
      ...page,
      index,
      title,
      label: pageLabel(index),
      name: pageName(page, index),
      galleryPages,
    });
  };

  const rotateDocumentPreview = (degrees) =>
    setDocumentPreviewRotation((current) => (current + degrees + 360) % 360);

  const navigateDocumentPreview = (direction) => {
    if (!documentPreview?.galleryPages?.length) return;
    const pages = documentPreview.galleryPages;
    const nextIndex = (documentPreview.index + direction + pages.length) % pages.length;
    openDocumentPreview(pages[nextIndex], nextIndex, documentPreview.title, pages);
  };

  const toggleDocumentSection = (sectionKey) =>
    setCollapsedDocumentSections((current) => ({
      ...current,
      [sectionKey]: !current[sectionKey],
    }));

  const documentSection = ({ sectionKey, icon, title, verified, rows, pages }) => {
    const collapsed = Boolean(collapsedDocumentSections[sectionKey]);
    const displayPages = pages.map((page, index) => ({
      ...page,
      name: page.name || pageLabel(index),
      caption: page.caption || title,
    }));
    return (
      <section className="care-staff-document-section">
        <button
          className="care-staff-document-section__header"
          type="button"
          onClick={() => toggleDocumentSection(sectionKey)}
          aria-expanded={!collapsed}
        >
          <div className="care-staff-document-section__title">
            <span><i className={`ti ${icon}`} /></span>
            <div>
              <strong>{title}</strong>
              {collapsed ? <small>Tap to expand and review these details.</small> : null}
            </div>
          </div>
          <div className="care-staff-document-section__state">
            <span>{collapsed ? "Closed" : "Open"}</span>
            <i className="ti ti-chevron-right" aria-hidden="true" />
          </div>
        </button>
        {!collapsed ? (
          <div className="care-staff-document-section__body">
            <div className="care-staff-document-section__summary">
              <div>
                <span>Status</span>
                <strong className={verified ? "is-verified" : "is-not-verified"}>{verified ? "Verified" : "Not Verified"}</strong>
              </div>
              <div>
                <span>Documents</span>
                <strong>{documentsLoading ? "Loading" : `${displayPages.length} uploaded page${displayPages.length === 1 ? "" : "s"}`}</strong>
              </div>
            </div>
            <ProfileDocumentSection
              detailsTitle={title === "Work Authorization" ? "Authorization Details" : "Identity Details"}
              rows={rows}
              pages={displayPages}
              documentPreviewTitle={title}
              onPreview={openDocumentPreview}
            />
          </div>
        ) : null}
      </section>
    );
  };
  const documentSections = (
    <>
      {documentSection({
        sectionKey: "identity",
        icon: "ti-id",
        title: "Identity Verification",
        verified: Boolean(identityVerification?.isVerified || identityVerification?.verified),
        rows: [
          ["Document type", titleCase(identityVerification?.identificationDocumentType)],
          ["Decision", titleCase(identityVerification?.verificationDecision)],
          ["Requires review", booleanLabel(identityVerification?.requiresReview)],
          ["Uploaded on", dateTimeLabel(identityVerification?.uploadedOn)],
          ["Validated on", dateTimeLabel(identityVerification?.validatedOn)],
          ["Verified on", dateTimeLabel(identityVerification?.verifiedOn)],
        ],
        pages: identityPages,
      })}
      {documentSection({
        sectionKey: "workAuthorization",
        icon: "ti-id-badge-2",
        title: "Work Authorization",
        verified: Boolean(workAuthorization?.isVerified || workAuthorization?.verified),
        rows: [
          ["Authorization type", titleCase(workAuthorization?.workAuthorizationType || workAuthorization?.type)],
          ["Capability", titleCase(workAuthorization?.capability)],
          ["Can work", booleanLabel(workAuthorization?.canWork)],
          ["Requires verification", booleanLabel(workAuthorization?.requiresVerification)],
          ["Has restrictions", booleanLabel(workAuthorization?.hasRestrictions)],
          ["Expiration date", workAuthorization?.expirationDate],
          ["Uploaded on", dateTimeLabel(workAuthorization?.uploadedOn)],
        ],
        pages: workAuthorizationPages,
      })}
    </>
  );

  const sectionConfigs = {
    availability: {
      title: "Work Availability",
      fields: [
        canViewSensitiveStaffDetails ? { name: "workAuthorization", label: "Work authorization" } : null,
        { name: "preferredShifts", label: "Preferred working shifts", type: "chips", options: shiftOptions },
        { name: "canWorkOn", label: "Can work on", type: "chips", options: workDayOptions },
        { name: "preferredHoursPerWeek", label: "Preferred hours per week" },
        { name: "travelRadius", label: "Commute distance (miles)" },
        {
          name: "transportation",
          label: "Transportation",
          type: "select",
          options: [
            ["", "Choose answer"],
            ["OWN_CAR", "Own car"],
            ["PUBLIC_TRANSPORT", "Public transport"],
            ["RIDE_SHARE", "Ride share"],
            ["OTHER", "Other"],
          ],
        },
      ].filter(Boolean),
    },
    addresses: {
      title: "Addresses",
      fields: [
        { name: "permanentAddress", label: "Permanent address", type: "address" },
        { name: "residentialAddress", label: "Residential address", type: "address" },
      ],
    },
    nextOfKin: {
      title: "Next Of Kin",
      fields: [
        { name: "nextOfKinName", label: "Name" },
        { name: "nextOfKinRelationship", label: "Relationship" },
        { name: "nextOfKinPhone", label: "Phone" },
        { name: "nextOfKinEmail", label: "Email" },
      ],
    },
    emergency: {
      title: "Emergency Contact",
      fields: [
        { name: "emergencyContactName", label: "Name" },
        { name: "emergencyContactRelationship", label: "Relationship" },
        { name: "emergencyContactPhone", label: "Phone" },
        { name: "emergencyContactEmail", label: "Email" },
      ],
    },
    health: {
      title: "Health",
      fields: [
        { name: "disability", label: "Disability", type: "select", options: [["", "Choose answer"], ["NO", "No"], ["YES", "Yes"], ["PREFER_NOT_TO_SAY", "Prefer not to say"]] },
        { name: "disabilityExplanation", label: "Disability details", type: "textarea" },
        { name: "chronicIllness", label: "Chronic illness", type: "select", options: [["", "Choose answer"], ["NO", "No"], ["YES", "Yes"], ["PREFER_NOT_TO_SAY", "Prefer not to say"]] },
        { name: "chronicIllnessExplanation", label: "Chronic illness details", type: "textarea" },
        { name: "allergies", label: "Allergies", type: "textarea" },
      ],
    },
    languages: {
      title: "Languages And Interests",
      fields: [
        { name: "languages", label: "Languages spoken", type: "chips", options: languageOptions },
        { name: "otherLanguage", label: "Other language" },
        { name: "hobbies", label: "Hobbies", type: "textarea" },
        { name: "caregiverStatement", label: "Caregiver statement", type: "textarea" },
        { name: "notes", label: "Notes", type: "textarea" },
      ],
    },
  };

  const openEditSection = (sectionKey) => {
    const config = sectionConfigs[sectionKey];
    if (!config) return;
    const nextForm = {};
    config.fields.forEach((field) => {
      const value = view.care[field.name];
      if (field.type === "chips") nextForm[field.name] = toList(value);
      else if (field.type === "address") nextForm[field.name] = structuredAddress(value);
      else nextForm[field.name] = value || "";
    });
    setEditSection(sectionKey);
    setEditForm(nextForm);
    setEditError("");
  };

  const updateEditField = (field, value) =>
    setEditForm((current) => ({ ...current, [field]: value }));

  const updateEditAddress = (field, key, value) =>
    setEditForm((current) => ({
      ...current,
      [field]: {
        ...structuredAddress(current[field]),
        [key]: value,
      },
    }));

  const applyEditAddress = (field, nextAddress) =>
    setEditForm((current) => ({
      ...current,
      [field]: {
        ...structuredAddress(current[field]),
        ...nextAddress,
      },
    }));

  const toggleEditChip = (field, value) =>
    setEditForm((current) => {
      const existing = toList(current[field]);
      return {
        ...current,
        [field]: existing.includes(value)
          ? existing.filter((item) => item !== value)
          : [...existing, value],
      };
    });

  const saveEditSection = async () => {
    const config = sectionConfigs[editSection];
    if (!config || !careProfileRecordId) return;
    setEditSaving(true);
    setEditError("");
    try {
      const updates = config.fields.map((field) => {
        let value = editForm[field.name];
        if (field.type === "chips") value = toList(value);
        if (field.type === "address") value = structuredAddress(value);
        return { name: `care.${field.name}`, value };
      });
      const updated = await careService.careProfiles.patchProperties(careProfileRecordId, updates);
      setProfile(updated || profile);
      setEditSection(null);
      setEditForm({});
    } catch (err) {
      setEditError(err?.response?.data?.message || err?.message || "Unable to update section.");
    } finally {
      setEditSaving(false);
    }
  };

  const renderEditField = (field) => {
    const value = editForm[field.name];
    if (field.type === "address") {
      const address = structuredAddress(value);
      return (
        <div className="care-staff-edit-address" key={field.name}>
          <strong>{field.label}</strong>
          <label className="resident-form-field full-span">
            Location search
            {appConfigLoading ? (
              <input type="text" disabled placeholder="Loading map address search..." />
            ) : (
              <GooglePlacesAutocompleteInput
                googleMapsApiKey={googleMapsApiKey}
                placeholder={googleMapsApiKey ? "Search address e.g. 100 Main St, Seattle" : "Enter address"}
                types={["address"]}
                value={address.search || address.addressLine1}
                onChange={(nextValue) => {
                  updateEditAddress(field.name, "search", nextValue);
                  if (!googleMapsApiKey) updateEditAddress(field.name, "addressLine1", nextValue);
                }}
                onResolvePlace={async ({ details, prediction }) => {
                  const resolvedAddress = details?.formatted_address || prediction?.description || address.search || "";
                  applyEditAddress(field.name, {
                    ...(details ? addressFieldsFromPlaceDetails(details) : {}),
                    search: resolvedAddress,
                    addressLine1: details ? addressFieldsFromPlaceDetails(details).addressLine1 || resolvedAddress : resolvedAddress,
                  });
                }}
              />
            )}
          </label>
          <div className="resident-address-grid">
            {addressFields.map(([key, label]) => (
              <label className={`resident-form-field resident-address-field resident-address-field-${key}`} key={key}>
                <span>{label}</span>
                <input value={address[key] || ""} onChange={(event) => updateEditAddress(field.name, key, event.target.value)} />
              </label>
            ))}
          </div>
        </div>
      );
    }
    if (field.type === "chips") {
      const selected = toList(value);
      return (
        <div className="resident-form-field full-span" key={field.name}>
          {field.label}
          <div className="care-profile-chip-row">
            {(field.options || []).map((option) => (
              <button
                className={`mini-btn${selected.includes(option) ? " active" : ""}`}
                type="button"
                onClick={() => toggleEditChip(field.name, option)}
                key={option}
              >
                <i className="ti ti-check" /> {option}
              </button>
            ))}
          </div>
        </div>
      );
    }
    if (field.type === "select") {
      return (
        <label className="resident-form-field" key={field.name}>
          {field.label}
          <select value={value || ""} onChange={(event) => updateEditField(field.name, event.target.value)}>
            {(field.options || []).map(([optionValue, label]) => (
              <option value={optionValue} key={optionValue || label}>{label}</option>
            ))}
          </select>
        </label>
      );
    }
    return (
      <label className="resident-form-field" key={field.name}>
        {field.label}
        {field.type === "textarea" ? (
          <textarea value={value || ""} onChange={(event) => updateEditField(field.name, event.target.value)} />
        ) : (
          <input
            value={value || ""}
            onChange={(event) => updateEditField(field.name, event.target.value)}
            placeholder={field.type === "list" ? "Separate multiple values with commas" : ""}
          />
        )}
      </label>
    );
  };

  return (
    <CareShell className="dashboard-content residents-page care-staff-details-page">
      <div className="residents-page-head">
        <div className="page-title">
          <h1>{view.name ? `${view.name} Care Staff Details` : "Care Staff Details"}</h1>
          <p>Home care employment details for {view.name || "this care staff member"}.</p>
        </div>
        <div className="care-staff-page-actions">
          <Link className="mini-btn" to="/staff">
            <i className="ti ti-arrow-left" /> Back to staff
          </Link>
        </div>
      </div>

      {loading ? <Panel title="Care Staff"><p>Loading care staff details...</p></Panel> : null}
      {error ? <Panel title="Care Staff"><p className="form-error">{error}</p></Panel> : null}

      {!loading && !error && profile ? (
        <>
          <section className="resident-hero">
            <div className="resident-hero-main">
              <span className="resident-avatar">{view.initials}</span>
              <div>
                <small>Care staff</small>
                <h2>{view.name}</h2>
                <p>{view.employeeNumber || "Employee number pending"} · {view.email || "No email"}</p>
              </div>
            </div>
            <div className="resident-hero-meta">
              <span className={`pill ${statusClass(view.status)}`}>{titleCase(view.status)}</span>
              <strong>{display(view.phone, "No phone")}</strong>
              <small>Onboarding stage: {titleCase(profile.onboardingStage || "Not added")}</small>
            </div>
          </section>

          <div className="resident-preview-grid">
            <DetailCard
              icon="ti-id"
              title="Identity"
              rows={[
                ["Employee number", view.employeeNumber],
                ["Name", view.name],
                ["Email", view.email],
                ["Phone", view.phone],
                canViewSensitiveStaffDetails ? {
                  label: "Staff Details",
                  value: "",
                  onClick: userProfileId ? () => setDrawer("user") : null,
                  docTo: userProfilePath,
                  editTo: editPath(userProfilePath),
                  snapshotOnClick: canCaptureStaffSnapshots && userProfileId ? () => confirmProfileSnapshotSave("user") : null,
                  snapshotSavedAt: userProfileSnapshot?.capturedAt,
                  snapshotSaving: snapshotSaving === "user",
                  snapshotViewOnClick: userProfileSnapshot ? () => setSnapshotDrawerType("user-profile") : null,
                  snapshotUnavailable: canCaptureStaffSnapshots && !userProfileSnapshot,
                } : null,
                {
                  label: "Profession Resume/CV",
                  value: "",
                  onClick: professionalProfileId ? () => setDrawer("professional") : null,
                  docTo: professionalProfilePath,
                  editTo: editPath(professionalProfilePath),
                  actionOnClick: professionalProfileId ? null : () => setProfessionalWorkspaceDrawerOpen(true),
                  actionLabel: "Add new Professional Profile",
                  snapshotOnClick: canCaptureStaffSnapshots && professionalProfileId ? () => confirmProfileSnapshotSave("professional") : null,
                  snapshotSavedAt: professionalProfileSnapshot?.capturedAt,
                  snapshotSaving: snapshotSaving === "professional",
                  snapshotViewOnClick: professionalProfileSnapshot ? () => setSnapshotDrawerType("professional-profile") : null,
                  snapshotUnavailable: canCaptureStaffSnapshots && !professionalProfileSnapshot,
                },
              ].filter(Boolean)}
            />
            <DetailCard
              icon="ti-briefcase"
              title="Work Availability"
              onEdit={() => openEditSection("availability")}
              rows={[
                canViewSensitiveStaffDetails ? ["Work authorization", titleCase(view.care.workAuthorization)] : null,
                ["Preferred working shifts", toList(view.care.preferredShifts)],
                ["Can work on", toList(view.care.canWorkOn)],
                ["Preferred hours per week", view.care.preferredHoursPerWeek],
                ["Commute distance (miles)", view.care.travelRadius],
                ["Transportation", titleCase(view.care.transportation)],
              ].filter(Boolean)}
            />
            {canViewSensitiveStaffDetails ? (
              <DetailCard
                icon="ti-map-pin"
                title="Addresses"
                onEdit={() => openEditSection("addresses")}
                rows={[
                  ["Permanent address", addressSummary(view.care.permanentAddress)],
                  ["Residential address", addressSummary(view.care.residentialAddress)],
                ]}
              />
            ) : null}
            <DetailCard
              icon="ti-address-book"
              title="Next Of Kin"
              onEdit={() => openEditSection("nextOfKin")}
              rows={[
                ["Name", view.care.nextOfKinName],
                ["Relationship", view.care.nextOfKinRelationship],
                ["Phone", view.care.nextOfKinPhone],
                ["Email", view.care.nextOfKinEmail],
              ]}
            />
            <DetailCard
              icon="ti-phone-call"
              title="Emergency Contact"
              onEdit={() => openEditSection("emergency")}
              rows={[
                ["Name", view.care.emergencyContactName],
                ["Relationship", view.care.emergencyContactRelationship],
                ["Phone", view.care.emergencyContactPhone],
                ["Email", view.care.emergencyContactEmail],
              ]}
            />
            <DetailCard
              icon="ti-heartbeat"
              title="Health"
              onEdit={() => openEditSection("health")}
              rows={[
                ["Disability", titleCase(view.care.disability)],
                ["Disability details", view.care.disabilityExplanation],
                ["Chronic illness", titleCase(view.care.chronicIllness)],
                ["Chronic illness details", view.care.chronicIllnessExplanation],
                ["Allergies", view.care.allergies],
              ]}
            />
            <DetailCard
              icon="ti-language"
              title="Languages And Interests"
              onEdit={() => openEditSection("languages")}
              rows={[
                ["Languages spoken", [...toList(view.care.languages).filter((item) => item !== "Other"), view.care.otherLanguage].filter(Boolean)],
                ["Hobbies", view.care.hobbies],
                ["Caregiver statement", view.care.caregiverStatement],
                ["Notes", view.care.notes],
              ]}
            />
            <DetailCard
              icon="ti-user-check"
              title="Roles"
              onEdit={canManageStaffRoles ? () => setRolesDrawerOpen(true) : null}
              rows={[["Roles", roleSummary(profile) || "No special roles"]]}
            />
          </div>
        </>
      ) : null}

      <Drawer
        open={drawer === "user"}
        onClose={() => setDrawer("")}
        title="User Profile Details"
        width="min(860px, 100vw)"
        className="olreadia-flow-drawer care-staff-related-drawer"
        destroyOnClose
      >
        {userProfileId ? (
          <UserProfilePage
            userIdOverride={userProfileId}
            embedded
            readOnly
            hideSensitiveInfo={!canViewSensitiveStaffDetails}
            hideDocumentSections
          />
        ) : null}
        {canViewSensitiveStaffDetails ? (
          <div className="care-staff-drawer-document-grid">
            {documentSections}
          </div>
        ) : null}
      </Drawer>

      <Drawer
        open={Boolean(snapshotDrawerType)}
        onClose={() => setSnapshotDrawerType("")}
        title={snapshotDrawerType === "professional-profile" ? "Professional Profile Snapshot" : "User Profile Snapshot"}
        width={snapshotDrawerType === "professional-profile" ? "min(980px, 100vw)" : "min(860px, 100vw)"}
        className="olreadia-flow-drawer care-staff-related-drawer care-staff-snapshot-drawer"
        destroyOnClose
      >
        {snapshotDrawerType ? (
          <CareStaffSnapshotPage careProfileIdOverride={careProfileRecordId} typeOverride={snapshotDrawerType} embedded />
        ) : null}
      </Drawer>

      <Drawer
        open={drawer === "professional"}
        onClose={() => setDrawer("")}
        title="Professional Profile Details"
        width="min(980px, 100vw)"
        className="olreadia-flow-drawer care-staff-related-drawer"
        destroyOnClose
      >
        {professionalProfileId ? (
          <ProfessionalProfileDetails
            profileIdOverride={professionalProfileId}
            embedded
            hidePromotions
            readOnly
          />
        ) : null}
      </Drawer>

      <Drawer
        open={professionalWorkspaceDrawerOpen}
        onClose={() => setProfessionalWorkspaceDrawerOpen(false)}
        title="Create Professional Profile"
        width="min(520px, 100vw)"
        className="olreadia-flow-drawer care-staff-related-drawer"
        destroyOnClose
      >
        <div className="care-staff-workspace-select">
          <p>Professional Profiles are created from a personal workspace.</p>
          {personalWorkspaces.length ? (
            <>
              <strong>Select a personal workspace</strong>
              <div className="care-staff-workspace-list">
                {personalWorkspaces.map((workspace) => (
                  <button
                    className="care-staff-workspace-option"
                    type="button"
                    key={workspace.id}
                    onClick={() => createProfessionalProfileFromWorkspace(workspace.id)}
                  >
                    <span>
                      <i className="ti ti-user-circle" />
                    </span>
                    <div>
                      <strong>{workspace.name || workspace.displayName || "Personal Workspace"}</strong>
                      <small>{workspace.id === selectedWorkspaceId ? "Current workspace" : "Switch and create profile"}</small>
                    </div>
                    <i className="ti ti-arrow-right" />
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="care-staff-workspace-empty">
              <strong>No personal workspace found</strong>
              <p>Create a personal workspace first, then create the Professional Profile there.</p>
              <button className="mini-btn care-profile-primary-action" type="button" onClick={createPersonalWorkspace}>
                <i className="ti ti-plus" /> Create Personal Workspace
              </button>
            </div>
          )}
        </div>
      </Drawer>

      <Drawer
        open={Boolean(editSection)}
        onClose={() => {
          if (!editSaving) {
            setEditSection(null);
            setEditForm({});
            setEditError("");
          }
        }}
        title={editSection ? `Edit ${sectionConfigs[editSection]?.title || "Section"}` : "Edit Section"}
        width="min(620px, 100vw)"
        className="olreadia-flow-drawer care-staff-section-edit-drawer"
        destroyOnClose
      >
        {editSection ? (
          <div className="care-staff-section-edit-form">
            {editError ? <p className="form-error">{editError}</p> : null}
            {(sectionConfigs[editSection]?.fields || []).map(renderEditField)}
            <div className="care-staff-section-edit-actions">
              <button className="mini-btn" type="button" onClick={() => setEditSection(null)} disabled={editSaving}>
                Cancel
              </button>
              <button className="mini-btn care-profile-primary-action" type="button" onClick={saveEditSection} disabled={editSaving}>
                <i className="ti ti-device-floppy" /> {editSaving ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        ) : null}
      </Drawer>

      <CareProfileRolesDrawer
        open={rolesDrawerOpen}
        onClose={() => setRolesDrawerOpen(false)}
        careProfile={profile}
        personName={view.name}
        onChange={(updated) => setProfile(updated || profile)}
      />

      {documentPreview ? (
        <div className="care-profile-document-dialog" role="dialog" aria-modal="true" aria-label={documentPreview.title || "Document preview"}>
          <button className="care-profile-document-dialog__backdrop" type="button" aria-label="Close document preview" onClick={() => setDocumentPreview(null)} />
          <div className="care-profile-document-dialog__panel">
            <div className="care-profile-document-dialog__head">
              <div>
                <strong>{documentPreview.title || "Document Preview"}</strong>
                <small>
                  {documentPreview.label} · {documentPreview.name}
                  {documentPreview.galleryPages?.length ? ` · ${documentPreview.index + 1} of ${documentPreview.galleryPages.length}` : ""}
                </small>
              </div>
              <div className="care-profile-document-dialog__actions">
                <button className="care-profile-document-tool-btn" type="button" aria-label="Rotate document left" title="Rotate left" onClick={() => rotateDocumentPreview(-90)}>
                  <i className="ti ti-rotate-2" />
                </button>
                <button className="care-profile-document-tool-btn" type="button" aria-label="Rotate document right" title="Rotate right" onClick={() => rotateDocumentPreview(90)}>
                  <i className="ti ti-rotate-clockwise-2" />
                </button>
                <button className="care-profile-document-tool-btn" type="button" aria-label="Reset document rotation" title="Reset rotation" onClick={() => setDocumentPreviewRotation(0)}>
                  <i className="ti ti-refresh" />
                </button>
                <button className="care-profile-document-tool-btn close" type="button" aria-label="Close document preview" title="Close" onClick={() => setDocumentPreview(null)}>
                  <i className="ti ti-x" />
                </button>
              </div>
            </div>
            <div className="care-profile-document-dialog__body">
              <button
                className="care-profile-document-nav-btn previous"
                type="button"
                onClick={() => navigateDocumentPreview(-1)}
                disabled={(documentPreview.galleryPages?.length || 0) < 2}
              >
                <i className="ti ti-chevron-left" /> Back
              </button>
              <div className={`care-profile-document-dialog__frame${documentPreviewRotation % 180 !== 0 ? " rotated" : ""}`}>
                {pageIsImage(documentPreview) ? (
                  <img src={documentPreview.signedUrl} alt="" style={{ transform: `rotate(${documentPreviewRotation}deg)` }} />
                ) : (
                  <iframe src={documentPreview.signedUrl} title={documentPreview.name || "Document preview"} style={{ transform: `rotate(${documentPreviewRotation}deg)` }} />
                )}
              </div>
              <button
                className="care-profile-document-nav-btn next"
                type="button"
                onClick={() => navigateDocumentPreview(1)}
                disabled={(documentPreview.galleryPages?.length || 0) < 2}
              >
                Next <i className="ti ti-chevron-right" />
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </CareShell>
  );
}
